/**
 * BuiltinVariablesRenderer - Start node built-in variables selection
 *
 * Allows users to enable/disable context variables like user_id, user_name, etc.
 * Note: This renderer does not support field mapping as it's for variable selection.
 */

import React from 'react';
import { Variable } from 'lucide-react';
import { useI18n } from '@/i18n';
import type { BuiltInVariables } from '@/types/imageWorkflow';
import type { BaseCustomRendererProps } from './types';

// Built-in variable definitions
const getBuiltinVariables = (t: any): { key: keyof BuiltInVariables; label: string; description: string }[] => [
  { key: 'user_id', label: t('imageWorkflows.builtinVariables.userId.label'), description: t('imageWorkflows.builtinVariables.userId.description') },
  { key: 'user_name', label: t('imageWorkflows.builtinVariables.userName.label'), description: t('imageWorkflows.builtinVariables.userName.description') },
  { key: 'user_email', label: t('imageWorkflows.builtinVariables.userEmail.label'), description: t('imageWorkflows.builtinVariables.userEmail.description') },
  { key: 'execution_id', label: t('imageWorkflows.builtinVariables.executionId.label'), description: t('imageWorkflows.builtinVariables.executionId.description') },
  { key: 'workflow_id', label: t('imageWorkflows.builtinVariables.workflowId.label'), description: t('imageWorkflows.builtinVariables.workflowId.description') },
  { key: 'workflow_name', label: t('imageWorkflows.builtinVariables.workflowName.label'), description: t('imageWorkflows.builtinVariables.workflowName.description') },
  { key: 'timestamp', label: t('imageWorkflows.builtinVariables.timestamp.label'), description: t('imageWorkflows.builtinVariables.timestamp.description') },
  { key: 'date', label: t('imageWorkflows.builtinVariables.date.label'), description: t('imageWorkflows.builtinVariables.date.description') },
  { key: 'time', label: t('imageWorkflows.builtinVariables.time.label'), description: t('imageWorkflows.builtinVariables.time.description') },
];

export const BuiltinVariablesRenderer: React.FC<BaseCustomRendererProps> = ({
  value,
  onChange,
  isDisabled = false,
}) => {
  const { t } = useI18n();
  const enabledBuiltins = (value as string[]) || [];
  const BUILTIN_VARIABLES = getBuiltinVariables(t);

  const handleToggle = (key: keyof BuiltInVariables, checked: boolean) => {
    const updated = checked
      ? [...enabledBuiltins, key]
      : enabledBuiltins.filter((k) => k !== key);
    onChange(updated);
  };

  return (
    <div className="p-4 border border-cyan-200 rounded-lg bg-cyan-50/50 space-y-3">
      <div className="flex items-center gap-2">
        <Variable className="w-4 h-4 text-cyan-600" />
        <span className="text-sm font-semibold text-cyan-700">{t('imageWorkflows.builtinVariables.title')}</span>
        <span className="text-xs text-cyan-500">{t('imageWorkflows.builtinVariables.selectToEnable')}</span>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {BUILTIN_VARIABLES.map((variable) => {
          const isEnabled = enabledBuiltins.includes(variable.key);
          return (
            <label
              key={variable.key}
              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                isEnabled ? 'bg-cyan-100' : 'bg-white hover:bg-gray-50'
              } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => handleToggle(variable.key, e.target.checked)}
                disabled={isDisabled}
                className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{variable.label}</span>
                  <code className="text-xs text-cyan-600 bg-cyan-100 px-1.5 py-0.5 rounded">
                    {`{{ctx.${variable.key}}}`}
                  </code>
                </div>
                <p className="text-xs text-gray-500 truncate">{variable.description}</p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default BuiltinVariablesRenderer;
